import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { CustomerService } from '../../services/customer.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';

/**
 * Customer Payments — every payment taken in a date range, with the invoices each
 * one was applied to underneath it.
 *
 * Reads apir/customers/payments. A payment row carries what came in (Net), what
 * has been put against documents (Applied) and what is still sitting on the
 * account (Remainder). Each allocation row underneath it carries the invoice's own
 * total, what THIS payment put against it, what EVERY payment has put against it,
 * and what is still open on it — which is how a short-paid invoice closed by a
 * second cheque stays readable.
 *
 * Balance is the running column: it starts at the payment and drops by each
 * application, so the last row of a group is the cash still unapplied. It is
 * computed here rather than in SQL because it only means anything in the order the
 * rows are displayed.
 *
 * Two search boxes, deliberately distinct:
 *
 *   Quick search   client-side over what is already loaded — instant, but only
 *                  sees the page (see the truncation note in the toolbar).
 *   Check # / ref  a server filter, applied on Apply, so it searches the whole
 *                  range before the row cap. This is the "find that cheque" box.
 *
 * Groups start collapsed: one wire can carry three hundred applications, and a
 * report that opens with fifteen thousand rows on screen is not a report. The
 * exports always contain the full detail regardless of what is expanded — they
 * are built from the data, not from the DOM.
 *
 * Runs in two places, following the same contract as its sibling reports:
 *
 *   menuapp = true    the standalone screen off Customers → Reports, with its own
 *                     card, header and customer typeahead.
 *   menuapp = false    embedded in the customer view's Reports tab, where the
 *                     account arrives on `customer` and the typeahead is dropped —
 *                     the page has already chosen who this is about.
 *
 * This is the customer-facing view. Receivables → Payment Report is the
 * back-office one (journals, voids, the bank side).
 */
@Component({
	selector: 'app-customer-payments-report',
	templateUrl: './customer-payments-report.component.html',
	styleUrls: ['./customer-payments-report.component.scss']
})
export class CustomerPaymentsReportComponent implements OnInit, OnChanges, OnDestroy {

	/** The account to report on when embedded. Doubles as the typeahead's pick. */
	@Input() customer: any = null;

	/** False when rendered inside the customer view's Reports tab. */
	@Input() menuapp: any = true;

	private destroy$ = new Subject<void>();

	filterForm: UntypedFormGroup;
	searcher = new UntypedFormControl('');

	// Too many accounts for a picker, so the customer filter is a typeahead
	// against the same search the rest of the app uses.
	customerSearch = new UntypedFormControl('');
	customerResults: any[] = [];
	customerSearching = false;
	private customerSub: any = null;

	loading = false;
	exporting = false;
	errorMessage = '';

	rows: any[] = [];
	searchresults: any[] = [];
	totals: any = null;
	truncated = false;
	limit = 250;

	paytypes: any[] = [];
	rowcaps = [250, 500, 1000, 2000];

	sort = '';
	dir: 'asc' | 'desc' = 'desc';

	expanded: { [id: number]: boolean } = {};

	constructor(
		private fb: UntypedFormBuilder,
		private customerService: CustomerService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
	) {}

	ngOnInit(): void {
		const today = new Date();
		const first = new Date(today.getFullYear(), today.getMonth(), 1);

		this.filterForm = this.fb.group({
			from: [this.isoDate(first)],
			to: [this.isoDate(today)],
			doctype: ['receipts'],
			paytype: [[]],
			unappliedonly: [false],
			search: [''],
			limit: [250],
		});

		this.searcher.valueChanges.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe(() => this.filterSearch());

		this.customerSearch.valueChanges.pipe(
			debounceTime(250),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe((v: any) => this.lookupCustomers(v));

		this.customerService.getCustomerPaymentsFilters().subscribe((r: any) => {
			if (!r?.success) { return; }
			this.paytypes = r.paytypes || [];
		});

		this.load();
	}

	/**
	 * The customer view keeps this component alive across accounts (its tab is
	 * preserveContent), so a new debtorno has to re-run the report rather than
	 * leave the previous account's payments on screen.
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (this.menuapp || !changes['customer'] || changes['customer'].firstChange) { return; }

		const before = changes['customer'].previousValue?.debtorno;
		if (this.customer?.debtorno && this.customer.debtorno !== before && this.filterForm) {
			this.load();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	load(): void {
		this.loading = true;
		this.errorMessage = '';
		this.expanded = {};

		this.customerService.getCustomerPaymentsReport(this.params()).subscribe((r: any) => {
			this.loading = false;
			if (!r?.success) {
				this.rows = [];
				this.searchresults = [];
				this.totals = null;
				this.errorMessage = r?.message || 'Could not load payments for this range.';
				return;
			}
			this.rows = this.withBalances(r.rows || []);
			this.totals = r.totals || null;
			this.truncated = !!r.truncated;
			this.limit = r.limit || this.limit;
			this.filterSearch();
		}, () => {
			this.loading = false;
			this.rows = [];
			this.searchresults = [];
			this.totals = null;
			this.errorMessage = 'Could not load payments for this range.';
		});
	}

	/**
	 * The running Balance column. Starts at the payment and drops by each
	 * application, so the figure beside the last invoice is the cash left on the
	 * account — the number this report exists to show.
	 */
	private withBalances(rows: any[]): any[] {
		for (const row of rows) {
			let balance = Number(row.net) || 0;
			row.balance = balance;
			for (const alloc of (row.allocations || [])) {
				balance = Math.round((balance - (Number(alloc.applied) || 0)) * 100) / 100;
				alloc.balance = balance;
			}
		}
		return rows;
	}

	/** Client-side overlay on the loaded page — instant, no round trip. */
	filterSearch(): void {
		const term = (this.searcher.value || '').toString().trim();
		if (!term) {
			this.searchresults = this.rows;
			return;
		}
		this.searchresults = this.globalSearchService.filterItem(this.rows, term,
			'debtorno,customer,transno,checknum,paytype,invtext,branchcode');
	}

	get searching(): boolean {
		return !!(this.searcher.value || '').toString().trim();
	}

	/**
	 * Tiles follow what is on screen: range totals printed next to a filtered grid
	 * read as an arithmetic error. Unfiltered, they are the server's totals for the
	 * whole range — which is the honest figure when the page itself is capped.
	 */
	get displayTotals(): any {
		if (!this.searching) { return this.totals; }
		if (!this.searchresults.length) { return null; }

		const sum = (k: string) => this.searchresults.reduce((a: number, r: any) => a + (Number(r[k]) || 0), 0);
		const accounts: any = {};
		this.searchresults.forEach((r: any) => { accounts[r.debtorno] = true; });

		return {
			payments: this.searchresults.length,
			customers: Object.keys(accounts).length,
			net: sum('net'),
			applied: sum('applied'),
			unapplied: sum('unapplied'),
		};
	}

	/**
	 * The summary line under the grid. Drops the account count when the screen is
	 * already scoped to one customer, and says "Range total" only when the detail
	 * above it is a capped page rather than the whole range.
	 */
	get totalsLabel(): string {
		const t = this.displayTotals;
		if (!t) { return ''; }

		const bits = [this.plural(t.payments, 'payment')];
		if (this.menuapp) { bits.push(this.plural(t.customers, 'account')); }

		const prefix = (this.truncated && !this.searching) ? 'Range total — ' : '';
		return prefix + bits.join(' · ');
	}

	/**
	 * Balance is a within-group countdown, so it only earns a column when some
	 * payment on screen actually spreads over more than one document. An account
	 * paying each invoice with its own card charge would otherwise get a column
	 * that just repeats Net on every row.
	 */
	get showBalance(): boolean {
		return this.searchresults.some((r: any) => Number(r.allocation_count) > 1);
	}

	/** Rounded to the cent: float noise on the ov* columns is not an open balance. */
	isZero(v: any): boolean {
		return Math.abs(Number(v) || 0) < 0.005;
	}

	/**
	 * Rendered column count. The Customer column is dropped when embedded — it
	 * would repeat the one account the whole tab is about on every row — so the
	 * empty-state and totals rows cannot hard-code their span.
	 */
	get colCount(): number {
		return (this.menuapp ? 13 : 12) - (this.showBalance ? 0 : 1);
	}

	/** Account count for the payments tile — noise when there is only ever one. */
	get accountsSub(): string | null {
		return this.menuapp && this.displayTotals ? this.plural(this.displayTotals.customers, 'account') : null;
	}

	private plural(n: any, word: string): string {
		const count = Number(n) || 0;
		return count.toLocaleString('en-US') + ' ' + word + (count === 1 ? '' : 's');
	}

	// ---- customer typeahead -----------------------------------------------

	/**
	 * The control holds free text until an option is picked; `customer` is what
	 * actually filters, so clearing the box clears the filter rather than leaving a
	 * stale account pinned to the report.
	 */
	lookupCustomers(term: any): void {
		// Material writes the picked option's object into the control before it emits
		// (optionSelected), and that write reaches this subscription a debounce later.
		// A non-string arrival is therefore the pick landing, not someone typing —
		// treating it as typed text reads as an empty box and clears the account that
		// was just chosen, so the next Apply silently reports every customer.
		if (typeof term !== 'string') {
			return;
		}

		const text = term.trim();

		if (this.customer && text !== this.customerLabel(this.customer)) {
			this.customer = null;
		}
		if (this.customerSub) { this.customerSub.unsubscribe(); }
		if (text.length < 2) {
			this.customerResults = [];
			this.customerSearching = false;
			return;
		}

		this.customerSearching = true;
		this.customerSub = this.customerService.getCustomerSearch({
			source: 'customerpayments',
			keywords: text,
		}).subscribe((r: any) => {
			this.customerSearching = false;
			this.customerResults = Array.isArray(r) ? r : [];
		}, () => { this.customerSearching = false; });
	}

	/**
	 * Typeahead results carry `name`; the customer view's customerdata carries it on
	 * `customer.name`. Falls back to the account alone rather than printing
	 * "4135 — undefined" into an export subtitle.
	 */
	customerLabel(c: any): string {
		if (!c) { return ''; }
		const name = c.name || c.customer?.name || '';
		return name ? c.debtorno + ' — ' + name : '' + c.debtorno;
	}

	pickCustomer(c: any): void {
		this.customer = c;
		this.customerSearch.setValue(this.customerLabel(c), { emitEvent: false });
		this.customerResults = [];
		this.load();
	}

	clearCustomer(): void {
		this.customer = null;
		this.customerResults = [];
		this.customerSearch.setValue('', { emitEvent: false });
		this.load();
	}

	// ---- groups ------------------------------------------------------------

	toggle(row: any): void {
		this.expanded[row.id] = !this.expanded[row.id];
	}

	isExpanded(row: any): boolean {
		return !!this.expanded[row.id];
	}

	expandAll(): void {
		this.searchresults.forEach((r: any) => {
			if (r.allocation_count) { this.expanded[r.id] = true; }
		});
	}

	collapseAll(): void {
		this.expanded = {};
	}

	get anyExpanded(): boolean {
		return Object.values(this.expanded).some((v) => v);
	}

	/**
	 * What went on the payment line's Text/Order cell. A receipt carries its pay
	 * type in `reference` and the cheque or auth number in `checknum`; the old
	 * report showed one, and both are what people search on.
	 */
	payRef(row: any): string {
		const parts = [row.paytype, row.checknum].filter((p: any) => !!(p || '').toString().trim());
		return parts.join(' · ');
	}

	sortBy(key: string): void {
		if (this.sort === key) {
			this.dir = this.dir === 'desc' ? 'asc' : 'desc';
		} else {
			this.sort = key;
			this.dir = 'desc';
		}
		this.load();
	}

	sortIcon(key: string): string {
		if (this.sort !== key) { return ''; }
		return this.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	// ---- export ------------------------------------------------------------

	get hasExportableRows(): boolean {
		return this.searchresults.length > 0;
	}

	private get exportTitle(): string {
		return 'Customer Payments';
	}

	/**
	 * What shaped the file: the range, the account, and any filter that narrowed it.
	 *
	 * Not the row cap. The tiles and the grand total are the whole range's whichever
	 * way the detail was capped, so a line about the cap explains a discrepancy the
	 * file does not actually contain — it reads as a warning about numbers that are
	 * in fact correct. The screen still says it, where raising the cap is one click.
	 */
	private exportSubtitle(): string {
		const f = this.filterForm.value;
		const bits = [this.longDate(f.from) + ' to ' + this.longDate(f.to)];
		if (this.customer) { bits.push(this.customerLabel(this.customer)); }
		if (f.doctype === 'credits') { bits.push('credit notes'); }
		if (f.doctype === 'both') { bits.push('receipts and credit notes'); }
		if (f.unappliedonly) { bits.push('unapplied only'); }
		if ((f.paytype || []).length) { bits.push((f.paytype || []).join(', ')); }
		if (f.search) { bits.push('matching "' + f.search + '"'); }
		if (this.searching) { bits.push('quick search "' + this.searcher.value + '"'); }
		return bits.join('  ·  ');
	}

	private get exportFilename(): string {
		const acct = this.customer ? '-' + this.customer.debtorno : '';
		return 'customer-payments' + acct + '-' + this.isoDate(new Date());
	}

	// ---- pdf ---------------------------------------------------------------

	/**
	 * The printed report.
	 *
	 * Not the screen grid. On screen the twelve columns are shared by payment rows
	 * and allocation rows, which works because you expand one group at a time and
	 * the indentation and colour tell you which is which. Flattened into a pdf that
	 * falls apart: `Net` holds a $316,763 wire on one row and a $911 invoice on the
	 * next under the same heading, the account number and customer name repeat down
	 * every line of the group, and a primary-key column leads the page. Five pages
	 * of that is a data dump, not a report.
	 *
	 * So print it the way a statement is printed — a banner per payment carrying
	 * everything true of the whole group (who, when, how, how much), and beneath it
	 * a table whose columns only ever mean one thing: the documents that payment
	 * paid. Nothing repeats, and every column heading is honest.
	 *
	 * One table per payment rather than one long table, for page breaks: a group
	 * that fits stays whole on its page (`.pg`), a 300-invoice wire is allowed to
	 * split, and wkhtmltopdf repeats each table's own <thead> when it does — so a
	 * split group still has its column labels on the next page. Column widths are
	 * fixed in a colgroup so the tables all line up as one grid down the page.
	 *
	 * Built from the data, never cloned from the DOM: collapsed groups are not in
	 * the DOM at all, and the allocation detail is the point of the file.
	 */
	private buildPdfHtml(): string {
		const cols = this.pdfCols();
		const span = cols.length;
		const bannerLeft = Math.max(1, span - 3);

		let html = this.pdfStyles();
		html += this.pdfSummary();

		for (const r of this.searchresults) {
			const allocs = r.allocations || [];
			// A wire spread over hundreds of invoices cannot be held on one page;
			// everything else should never be split across one.
			html += '<div class="pg' + (allocs.length > 14 ? ' pg-big' : '') + '">';
			html += '<table class="pay"><colgroup>'
				+ cols.map((c: any) => '<col style="width:' + c.w + '%">').join('')
				+ '</colgroup>';

			html += '<thead>';
			html += '<tr class="ban">'
				+ '<td colspan="' + bannerLeft + '">' + this.pdfBannerLeft(r) + '</td>'
				+ '<td colspan="' + (span - bannerLeft) + '" class="ban-r">'
				+ this.pdfBannerRight(r) + '</td>'
				+ '</tr>';
			if (allocs.length) {
				html += '<tr class="lbl">'
					+ cols.map((c: any) => '<td' + (c.num ? ' class="n"' : '') + '>'
						+ this.esc(c.label) + '</td>').join('')
					+ '</tr>';
			}
			html += '</thead><tbody>';

			if (!allocs.length) {
				html += '<tr class="al"><td colspan="' + span + '" class="none">'
					+ 'Nothing applied — the full amount is sitting on the account.'
					+ '</td></tr>';
			}

			for (const a of allocs) {
				html += '<tr class="al">'
					+ cols.map((c: any) => '<td' + this.pdfCellClass(c, a) + '>'
						+ c.cell(a, r) + '</td>').join('')
					+ '</tr>';
			}

			// Only earns its keep when the payment actually spread: on a one-invoice
			// cheque it would repeat the line above it.
			if (allocs.length > 1) {
				html += '<tr class="gt">'
					+ '<td colspan="4">' + this.esc(this.plural(allocs.length, 'document')) + ' paid</td>'
					+ '<td class="n"></td>'
					+ '<td class="n">' + this.money(r.applied) + '</td>'
					+ '<td class="n" colspan="' + (span - 6) + '">'
					+ (this.isZero(r.unapplied) ? '' : this.money(r.unapplied) + ' left on account')
					+ '</td></tr>';
			}

			html += '</tbody></table></div>';
		}

		if (!this.searchresults.length) {
			html += '<div class="cpr-empty">No payments in this range.</div>';
		}

		return html + this.pdfGrandTotal();
	}

	/**
	 * The printed columns. Every one describes a document the payment paid — the
	 * payment's own money lives in the banner, so nothing here is overloaded.
	 *
	 * Remaining is the within-group countdown. It only appears when some payment in
	 * the report spread over more than one document — otherwise every row of it would
	 * simply repeat the banner.
	 */
	private pdfCols(): any[] {
		const cols: any[] = [
			{ label: 'Document', w: 12, cell: (a: any) => this.esc(a.transno) },
			{ label: 'Date', w: 10, cell: (a: any) => this.esc(this.usDate(a.trandate)) },
			{ label: 'Type', w: 13, cell: (a: any) => this.esc(a.typename) },
			{ label: 'Order #', w: 11, cell: (a: any) => this.esc(Number(a.order_) > 1 ? a.order_ : '') },
			{ label: 'Document total', w: 14, num: true, cell: (a: any) => this.money(a.net) },
			{ label: 'This payment', w: 14, num: true, cell: (a: any) => this.money(a.applied) },
			{ label: 'Total applied', w: 13, num: true, cell: (a: any) => this.money(a.total_alloc) },
			{ label: 'Still open', w: 13, num: true, open: true, cell: (a: any) => this.money(a.remainder) },
		];

		if (this.showBalance) {
			// Filled on every row, including the one-document payments the screen
			// leaves blank. The column is only here because some payment in the report
			// spread, and on a single-document group the countdown still says something
			// worth reading — what the cheque left on the account.
			cols.push({
				label: 'Remaining', w: 12, num: true, dim: true,
				cell: (a: any) => this.money(a.balance),
			});
			// 9 columns of a fixed 100%: give the countdown its width back from the
			// text side, which has slack, rather than from the money columns.
			cols[0].w = 10; cols[1].w = 9; cols[2].w = 11; cols[3].w = 10;
			cols[4].w = 12; cols[5].w = 12; cols[6].w = 12; cols[7].w = 12;
		}

		return cols;
	}

	/** Right-aligns money, reds an invoice this payment did not close, dims the countdown. */
	private pdfCellClass(col: any, a: any): string {
		const c = [];
		if (col.num) { c.push('n'); }
		if (col.open && !this.isZero(a.remainder)) { c.push('open'); }
		if (col.dim) { c.push('dim'); }
		return c.length ? ' class="' + c.join(' ') + '"' : '';
	}

	/** Who and what: the identity of the payment, said once for the whole group. */
	private pdfBannerLeft(r: any): string {
		let html = '<span class="b-date">' + this.esc(this.usDate(r.trandate)) + '</span>'
			+ '<span class="b-sep">·</span>'
			+ '<span class="b-type">' + this.esc(r.typename || 'Payment') + '</span>'
			+ '<span class="b-no">' + this.esc(r.transno) + '</span>';

		const ref = this.payRef(r);
		if (ref) { html += '<span class="b-ref">' + this.esc(ref) + '</span>'; }

		const who = this.menuapp
			? [r.debtorno, r.customer].filter((v: any) => !!v).join(' — ')
			: '';
		if (who) { html += '<div class="b-acct">' + this.esc(who) + '</div>'; }
		if (r.invtext) { html += '<div class="b-memo">' + this.esc(r.invtext) + '</div>'; }

		return html;
	}

	/** The payment's own money, labelled as the payment's — never as an invoice's. */
	private pdfBannerRight(r: any): string {
		let html = '<div class="b-net"><span>Received</span> ' + this.money(r.net) + '</div>';
		const bits = ['Applied ' + this.money(r.applied)];
		if (!this.isZero(r.unapplied)) {
			bits.push('<span class="b-open">On account ' + this.money(r.unapplied) + '</span>');
		}
		html += '<div class="b-sub">' + bits.join(' · ') + '</div>';
		return html;
	}

	/** The tiles from the screen, printed — the shape of the range before the detail. */
	private pdfSummary(): string {
		const t = this.displayTotals;
		if (!t) { return ''; }

		const cell = (label: string, value: string, sub: string) =>
			'<td><div class="s-k">' + this.esc(label) + '</div>'
			+ '<div class="s-v">' + value + '</div>'
			+ '<div class="s-s">' + this.esc(sub) + '</div></td>';

		return '<table class="cpr-sum"><tr>'
			+ cell('Payments', Number(t.payments).toLocaleString('en-US'),
				this.menuapp ? this.plural(t.customers, 'account') : '')
			+ cell('Received', this.money(t.net), '')
			+ cell('Applied', this.money(t.applied), '')
			+ cell('On account', this.money(t.unapplied),
				this.isZero(t.unapplied) ? 'fully applied' : 'unapplied cash')
			+ '</tr></table>';
	}

	private pdfGrandTotal(): string {
		const t = this.displayTotals;
		if (!t || !this.searchresults.length) { return ''; }

		return '<table class="cpr-grand"><tr>'
			+ '<td>' + this.esc(this.totalsLabel) + '</td>'
			+ '<td class="n">Received ' + this.money(t.net) + '</td>'
			+ '<td class="n">Applied ' + this.money(t.applied) + '</td>'
			+ '<td class="n">On account ' + this.money(t.unapplied) + '</td>'
			+ '</tr></table>';
	}

	/**
	 * Print styles, inline because the file is rendered by wkhtmltopdf out of a
	 * shared header template that knows nothing about this report.
	 *
	 * Selectors are prefixed and `!important` where the template's own `td` rule
	 * (padding, border-top on every cell) would otherwise put a grid back on top of
	 * a layout built out of bands.
	 */
	private pdfStyles(): string {
		return '<style>'
			// The shared print header sets h5 heavier and larger than the h4 above it,
			// so the filter line shouts over the report name. Corrected here rather
			// than in the template, which every other export renders through.
			+ 'body h4{font-size:16px !important;font-weight:700 !important;color:#1F2B3E;'
			+ 'margin:0 0 2px !important;line-height:20px !important}'
			+ 'body h5{font-size:9.5px !important;font-weight:400 !important;color:#6B7A8D;'
			+ 'margin:0 0 8px !important;line-height:13px !important}'

			+ '.cpr-sum{width:100%;border-collapse:separate;border-spacing:6px 0;margin:2px 0 10px;table-layout:fixed}'
			+ '.cpr-sum td{border:1px solid #D3DBE4 !important;border-top:1px solid #D3DBE4 !important;'
			+ 'background:#F5F7FA;padding:6px 9px !important;vertical-align:top}'
			+ '.cpr-sum .s-k{font-size:8px;letter-spacing:.08em;text-transform:uppercase;color:#6B7A8D;line-height:11px}'
			+ '.cpr-sum .s-v{font-size:15px;font-weight:700;color:#1F2B3E;line-height:19px}'
			+ '.cpr-sum .s-s{font-size:8px;color:#8494A5;line-height:11px}'

			+ '.cpr-empty{font-size:11px;color:#6B7A8D;text-align:center;padding:24px 0}'

			+ '.pg{page-break-inside:avoid;margin:0 0 7px}'
			+ '.pg-big{page-break-inside:auto}'
			+ 'table.pay{width:100%;border-collapse:collapse;table-layout:fixed;font-size:9px}'
			+ 'table.pay td{padding:2px 5px !important;border-top:none !important;line-height:12px;'
			+ 'vertical-align:top;overflow:hidden}'
			+ 'table.pay td.n{text-align:right}'

			+ 'table.pay tr.ban td{background:#2C3A4B;color:#fff;padding:4px 7px !important;'
			+ 'border-bottom:1px solid #2C3A4B !important}'
			+ 'tr.ban .b-date{font-size:10px;font-weight:700}'
			+ 'tr.ban .b-sep{color:#8FA3B8;padding:0 4px}'
			+ 'tr.ban .b-type{font-size:10px}'
			+ 'tr.ban .b-no{font-size:10px;font-weight:700;padding-left:4px}'
			+ 'tr.ban .b-ref{font-size:9px;color:#C6D3E0;padding-left:8px}'
			+ 'tr.ban .b-acct{font-size:10px;font-weight:700;padding-top:1px}'
			+ 'tr.ban .b-memo{font-size:8px;color:#B9C7D6;padding-top:1px}'
			+ 'tr.ban td.ban-r{text-align:right}'
			+ 'tr.ban .b-net{font-size:12px;font-weight:700;line-height:15px}'
			+ 'tr.ban .b-net span{font-size:8px;font-weight:400;color:#B9C7D6;'
			+ 'text-transform:uppercase;letter-spacing:.06em}'
			+ 'tr.ban .b-sub{font-size:8px;color:#C6D3E0;line-height:11px}'
			+ 'tr.ban .b-open{color:#FFC9A8;font-weight:700}'

			+ 'table.pay tr.lbl td{background:#E9EDF2;color:#4A5A6C;font-size:7.5px;font-weight:700;'
			+ 'text-transform:uppercase;letter-spacing:.05em;line-height:10px;'
			+ 'border-bottom:1px solid #C9D2DC !important}'

			+ 'table.pay tr.al td{border-bottom:1px solid #EDF0F4 !important;color:#1F2B3E}'
			+ 'table.pay tr.al td.open{color:#9A2B23;font-weight:700}'
			+ 'table.pay tr.al td.dim{color:#7C8B9C}'
			+ 'table.pay tr.al td.none{color:#6B7A8D;font-style:italic;padding:3px 7px !important}'

			+ 'table.pay tr.gt td{background:#F3F5F8;font-weight:700;color:#3B4A5C;font-size:8.5px;'
			+ 'border-top:1px solid #C9D2DC !important}'

			+ '.cpr-grand{width:100%;border-collapse:collapse;table-layout:fixed;margin:8px 0 0;'
			+ 'page-break-inside:avoid}'
			+ '.cpr-grand td{background:#1F2B3E;color:#fff;font-size:10px;font-weight:700;'
			+ 'padding:6px 8px !important;border-top:none !important}'
			+ '.cpr-grand td.n{text-align:right;font-weight:400}'
			+ '</style>';
	}

	// ---- xls ---------------------------------------------------------------

	/**
	 * The spreadsheet, which wants the opposite of the pdf: one flat row per fact,
	 * every row self-describing, so it can be filtered, sorted and pivoted without
	 * the reader having to look upward for context.
	 *
	 * So each allocation row repeats its payment's identity — date, number, cheque,
	 * account, name — and a `Row` column separates the payment lines from the
	 * document lines. No merged cells, no group banners, no totals row in the middle
	 * of the data.
	 *
	 * Money carries `$` and thousands separators here; apir/print's xls path strips
	 * both before PHPExcel sees the table, so Excel gets numbers it can add up.
	 */
	private buildXlsHtml(): string {
		const head = [
			'Row', 'Payment date', 'Payment #', 'Pay type', 'Check / ref', 'Account', 'Customer',
			'Received', 'Applied', 'On account',
			'Document', 'Document date', 'Document type', 'Order #',
			'Document total', 'This payment', 'Total applied', 'Still open', 'Remaining',
		];
		const firstNum = 7;
		const num = ' style="text-align:right"';

		let html = '<table border="1" cellspacing="0" cellpadding="4" '
			+ 'style="border-collapse:collapse;font-size:11px">';
		html += '<thead><tr>';
		head.forEach((h, i) => {
			html += '<th style="background-color:#3B4A5C;color:#FFFFFF;text-align:'
				+ (i >= firstNum ? 'right' : 'left') + '">' + this.esc(h) + '</th>';
		});
		html += '</tr></thead><tbody>';

		for (const r of this.searchresults) {
			const spread = Number(r.allocation_count) > 1;
			// Repeated onto every allocation row: the whole point of the flat shape.
			const ident = '<td>' + this.esc(this.shortDate(r.trandate)) + '</td>'
				+ '<td>' + this.esc(r.transno) + '</td>'
				+ '<td>' + this.esc(r.paytype) + '</td>'
				+ '<td>' + this.esc(r.checknum) + '</td>'
				+ '<td>' + this.esc(r.debtorno) + '</td>'
				+ '<td>' + this.esc(r.customer) + '</td>';

			html += '<tr style="background-color:#EEF1F4;font-weight:bold">'
				+ '<td>Payment</td>' + ident
				+ '<td' + num + '>' + this.money(r.net) + '</td>'
				+ '<td' + num + '>' + this.money(r.applied) + '</td>'
				+ '<td' + num + '>' + this.money(r.unapplied) + '</td>'
				+ '<td>' + this.esc(r.typename) + '</td>'
				+ '<td></td><td></td><td></td>'
				+ '<td' + num + '></td><td' + num + '></td>'
				+ '<td' + num + '></td><td' + num + '></td><td' + num + '></td>'
				+ '</tr>';

			for (const a of (r.allocations || [])) {
				html += '<tr>'
					+ '<td>Applied to</td>' + ident
					+ '<td' + num + '></td><td' + num + '></td><td' + num + '></td>'
					+ '<td>' + this.esc(a.transno) + '</td>'
					+ '<td>' + this.esc(this.shortDate(a.trandate)) + '</td>'
					+ '<td>' + this.esc(a.typename) + '</td>'
					+ '<td>' + this.esc(Number(a.order_) > 1 ? a.order_ : '') + '</td>'
					+ '<td' + num + '>' + this.money(a.net) + '</td>'
					+ '<td' + num + '>' + this.money(a.applied) + '</td>'
					+ '<td' + num + '>' + this.money(a.total_alloc) + '</td>'
					+ '<td' + num + '>' + this.money(a.remainder) + '</td>'
					+ '<td' + num + '>' + (spread ? this.money(a.balance) : '') + '</td>'
					+ '</tr>';
			}
		}

		const t = this.displayTotals;
		if (t) {
			html += '<tr style="font-weight:bold;background-color:#E2E7EC">'
				+ '<td colspan="7">' + this.esc(this.totalsLabel) + '</td>'
				+ '<td' + num + '>' + this.money(t.net) + '</td>'
				+ '<td' + num + '>' + this.money(t.applied) + '</td>'
				+ '<td' + num + '>' + this.money(t.unapplied) + '</td>'
				+ '<td colspan="9"></td>'
				+ '</tr>';
		}

		return html + '</tbody></table>';
	}

	exportXls(): void {
		this.exporting = true;
		const data = {
			content: this.globalSearchService.base64encode(this.buildXlsHtml()),
			filename: this.exportFilename,
			title: this.exportTitle,
			subtitle: this.exportSubtitle(),
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	exportPdf(): void {
		this.exporting = true;
		const data = {
			content: this.globalSearchService.base64encode(this.buildPdfHtml()),
			filename: this.exportFilename,
			title: this.exportTitle,
			subtitle: this.exportSubtitle(),
			// Nine columns plus the banner do not fit portrait.
			landscape: true,
			// Five pages of payment groups with no page numbers cannot be collated
			// once printed. Opt-in flag: apir/print leaves other reports alone.
			footer: 1,
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	// ---- small helpers -----------------------------------------------------

	private money(v: any): string {
		const n = Number(v) || 0;
		const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		// A minus sign rather than accounting parentheses: the xls path strips
		// currency punctuation, and "(123.45)" would survive it as text.
		return (n < 0 ? '-$' : '$') + s;
	}

	/** MM/DD/YYYY, matching the grid. The printed report is read by people, not sorted. */
	private usDate(v: any): string {
		const iso = this.shortDate(v);
		if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) { return iso; }
		return iso.substr(5, 2) + '/' + iso.substr(8, 2) + '/' + iso.substr(0, 4);
	}

	/** "Aug 1, 2026" for the export subtitle — a title line is not a data column. */
	private longDate(v: any): string {
		const iso = ('' + (v || '')).substr(0, 10);
		if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) { return iso; }
		const parts = iso.split('-').map((n) => Number(n));
		return new Date(parts[0], parts[1] - 1, parts[2])
			.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
	}

	private shortDate(v: any): string {
		if (!v) { return ''; }
		// Already a calendar date: hand it straight back. new Date('2026-08-18') is
		// parsed as UTC midnight, which prints as the 17th anywhere west of
		// Greenwich — a payment dated a day early is not a cosmetic problem.
		const iso = ('' + v).substr(0, 10);
		if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) { return iso; }
		const d = new Date(('' + v).replace(' ', 'T'));
		return isNaN(d.getTime()) ? iso : this.isoDate(d);
	}

	private esc(v: any): string {
		return ('' + (v === null || v === undefined ? '' : v))
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	}

	/** Local calendar date. toISOString() would shift the 1st back a day west of UTC. */
	private isoDate(d: Date): string {
		const m = ('0' + (d.getMonth() + 1)).slice(-2);
		const day = ('0' + d.getDate()).slice(-2);
		return d.getFullYear() + '-' + m + '-' + day;
	}

	/** Multi-selects travel as comma lists; the server whitelists them into an IN (). */
	private params(): any {
		const f = this.filterForm.value;
		return {
			from: f.from,
			to: f.to,
			doctype: f.doctype,
			paytype: (f.paytype || []).join(','),
			unappliedonly: f.unappliedonly ? 1 : '',
			search: f.search || '',
			debtorno: this.customer?.debtorno || '',
			limit: f.limit || 250,
			sort: this.sort,
			dir: this.dir,
		};
	}
}
